import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { isNil } from 'lodash';
// Import Types
import type { CampaignBudget } from 'types';
// Import Constants
import { l } from 'constants/common';
// Import Enums
import { BudgetLimitTypeEnum, BudgetPeriodTypeEnum, LimitBehaviorEnum } from 'enums';

const PERIOD_LABEL_KEYS: Record<BudgetPeriodTypeEnum, string> = {
    [BudgetPeriodTypeEnum.Day]: l.BudgetPeriodExecution,
    [BudgetPeriodTypeEnum.Week]: l.BudgetPeriodWeek,
    [BudgetPeriodTypeEnum.Month]: l.BudgetPeriodMonth,
    [BudgetPeriodTypeEnum.Year]: l.BudgetPeriodYear,
    [BudgetPeriodTypeEnum.Lifetime]: l.BudgetPeriodLifetime,
};

const BEHAVIOR_LABEL_KEYS: Record<LimitBehaviorEnum, string> = {
    [LimitBehaviorEnum.Warn]: l.LimitBehaviorWarn,
    [LimitBehaviorEnum.WarnAndStop]: l.LimitBehaviorWarnAndStop,
};

const LIMIT_TYPE_LABEL_KEYS: Record<BudgetLimitTypeEnum, string> = {
    [BudgetLimitTypeEnum.Count]: l.CountLimit,
    [BudgetLimitTypeEnum.Amount]: l.AmountLimit,
};

interface Props {
    budget: Nullable<CampaignBudget>;
}

const BudgetLimitsInfo = ({ budget }: Props) => {
    const { t } = useTranslation();

    if (isNil(budget)) {
        return null;
    }

    const limitTypeLabel = LIMIT_TYPE_LABEL_KEYS[budget.LimitType as BudgetLimitTypeEnum] ?? t(l.NA);
    const periodLabel =
        !isNil(budget.Period) && PERIOD_LABEL_KEYS[budget.Period as BudgetPeriodTypeEnum]
            ? t(PERIOD_LABEL_KEYS[budget.Period as BudgetPeriodTypeEnum])
            : null;
    const behaviorLabel =
        !isNil(budget.Behavior) && BEHAVIOR_LABEL_KEYS[budget.Behavior as LimitBehaviorEnum]
            ? t(BEHAVIOR_LABEL_KEYS[budget.Behavior as LimitBehaviorEnum])
            : null;

    return (
        <>
            <div className="key-value-h">
                <span className="ellipsis-text kv-label">{t(l.BonusBudget)}</span>
                <span className="ellipsis-text kv-value kv-value-css">{t(limitTypeLabel)}</span>
            </div>
            {!isNil(budget.LimitValue) && (
                <div className="key-value-h">
                    <span className="ellipsis-text kv-label">{t(l.LimitValue)}</span>
                    <span className="ellipsis-text kv-value kv-value-css">
                        {budget.LimitValue + (budget.CurrencyId ? ` ${budget.CurrencyId}` : '')}
                    </span>
                </div>
            )}
            {!isNil(behaviorLabel) && (
                <div className="key-value-h">
                    <span className="ellipsis-text kv-label">{t(l.LimitBehavior)}</span>
                    <span className="ellipsis-text kv-value kv-value-css">{behaviorLabel}</span>
                </div>
            )}
            {!isNil(periodLabel) && (
                <div className="key-value-h">
                    <span className="ellipsis-text kv-label">{t(l.BudgetPeriod)}</span>
                    <span className="ellipsis-text kv-value kv-value-css">{periodLabel}</span>
                </div>
            )}
        </>
    );
};

export default memo(BudgetLimitsInfo);
